import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
  useEffect,
  useSyncExternalStore,
  useRef,
} from "react";
import { type FrontendExperimentsResult } from "@atlaskit/feature-gate-js-client";
import { ConfigClient } from "@atlaskit/web-config-client";
import { PropsWithChildren } from "react";
import FeatureGates, { Identifiers } from "@atlaskit/feature-gate-js-client";
import { useInteractionContext } from "@atlaskit/react-ufo/interaction-context";
import { FeatureGatesInitializationWithProvider } from "@atlassian/feature-gates-react";
import { pollingProvider, featureGatesOptions } from "./featureGateProvider";
import { bifrostEnv, getLocale } from "../config";
import { UserAccount } from "../clients/gateway-client/types";
import { Site } from "../hooks/useSite";
import { logError } from "../utils/logger";
import { STATSIG_FEATURE_GATES } from "./statsigFeatureGates";
import { setPlatformFeatureFlagResolver } from "../utils/platformFeatureFlagResolver";
import { useManifestVersion } from "../hooks/useManifestVersion";
import { useSendAnalyticEvent } from "../analytics/analyticsClient";
import { ANALYTICS_OPERATIONAL_EVENT_ACTION_SUBJECTS } from "../analytics/analyticsEvents";
import { statsigStore } from "./statsigStore";
import { STATSIG_DYNAMIC_CONFIGS } from "./statsigFeatureGates";
import { getStatsigUrl, getSwitcherooUrl } from "../config/config";

const isProd = bifrostEnv === "prod";

type ConfigClientInstance = Awaited<ReturnType<typeof ConfigClient.fetch>>;

let configClientInstance: ConfigClientInstance | null = null;

async function initializeConfigClient(identifiers: Identifiers): Promise<void> {
  try {
    if (!process.env.STATSIG_API_KEY) {
      throw new Error("STATSIG_API_KEY not provided");
    }

    const ffsBaseUrl = isProd ? "https://api.atlassian.com/flags" : "https://api.stg.atlassian.com/flags";

    configClientInstance = await ConfigClient.fetch({
      ffsBaseUrl,
      ffsApiKey: process.env.STATSIG_API_KEY,
      context: {
        namespace: "rovo-extension_web",
        identifiers,
        metadata: {
          environment: bifrostEnv,
        },
      },
    });
  } catch (error) {
    logError({ error, impact: "Failed to initialize config client" });
  } finally {
    statsigStore.emitChange();
  }
}

export const getConfigClient = (): ConfigClientInstance | null => configClientInstance;

export function StatSigInitialization({
  site,
  children,
  userAccount,
  analyticsAnonymousId,
  onInitialized,
}: PropsWithChildren<{
  site?: Site;
  userAccount?: UserAccount;
  analyticsAnonymousId?: string;
  onInitialized?: () => void;
}>): React.JSX.Element {
  const atlassianAccountId = userAccount?.accountId;
  const ufoContext = useInteractionContext();
  const releaseHoldRef = useRef<(() => void) | null>(null);

  const locale = getLocale();
  const { browserManifestVersion } = useManifestVersion();
  const sendAnalyticEvent = useSendAnalyticEvent();

  // Set up provider callback to trigger store updates when feature gates change
  useEffect(() => {
    pollingProvider.setClientVersion("5.5.7");

    pollingProvider.setApplyUpdateCallback((_experimentsResult: FrontendExperimentsResult) => {
      statsigStore.emitChange();
    });
  }, []);

  const featureGatesIdentifiers = useMemo(
    () => ({
      ...(atlassianAccountId ? { atlassianAccountId } : {}),
      ...(analyticsAnonymousId ? { analyticsAnonymousId } : {}),
      tenantId: site?.workspace.cloudId,
      atlassianOrgId: site?.workspace.organisationId,
    }),
    [atlassianAccountId, analyticsAnonymousId, site],
  );

  const customAttributes = useMemo(
    () => ({
      tenantId: site?.workspace.cloudId ?? "",
      atlassianOrgId: site?.workspace.organisationId ?? "",
      extensionLocale: locale,
      extensionVersion: browserManifestVersion,
    }),
    [site, locale, browserManifestVersion],
  );

  // Use programmatic UFO hold for StatSig initialization
  React.useEffect(() => {
    const releaseHold = ufoContext?.hold("feature-gates-statsig-initialization");
    releaseHoldRef.current = releaseHold ?? null;

    return (): void => {
      // Cleanup: release hold if component unmounts before initialization completes
      releaseHoldRef.current?.();
      releaseHoldRef.current = null;
    };
  }, [ufoContext]);

  const handleOnReady = React.useCallback(
    (err?: Error): void => {
      // Release the UFO hold when initialization completes
      releaseHoldRef.current?.();
      releaseHoldRef.current = null;

      if (err) {
        // https://data-portal.internal.atlassian.com/analytics/registry/80196
        sendAnalyticEvent({
          eventType: "operational",
          event: {
            action: "failed",
            actionSubject: ANALYTICS_OPERATIONAL_EVENT_ACTION_SUBJECTS.FG_CLIENT_INITIALIZATION,
            source: "extensionFrontend",
            attributes: {
              error: err.message,
            },
          },
          userId: atlassianAccountId ?? "",
        });
      } else {
        void initializeConfigClient(featureGatesIdentifiers);
        onInitialized?.();
      }
    },
    [sendAnalyticEvent, atlassianAccountId, featureGatesIdentifiers, onInitialized],
  );

  return (
    <FeatureGatesInitializationWithProvider
      customAttributes={customAttributes}
      enabled
      identifiers={featureGatesIdentifiers}
      onReady={handleOnReady}
      options={featureGatesOptions}
      provider={pollingProvider}
    >
      <StatSigUpdateProvider>{children}</StatSigUpdateProvider>
    </FeatureGatesInitializationWithProvider>
  );
}

export const StatSigContext = createContext<{ refreshStatSigContext: () => void; contextState: boolean } | undefined>(
  undefined,
);

// exported for testing only
export const StatSigUpdateProvider = ({ children }: PropsWithChildren<{}>): React.JSX.Element => {
  // Get the current statsig store value
  const value = useSyncExternalStore(statsigStore.subscribe, statsigStore.getSnapshot);

  const [updateState, setUpdateState] = useState(false);
  // using a boolean state to force a re-render of components that depend on useCheckStatSigFeatureGate. the boolean value itself has no meaning
  const refreshStatSigContext = (): void => setUpdateState((state) => !state);

  // refresh context if external store value changes
  useEffect(() => {
    refreshStatSigContext();
  }, [value]);

  return (
    <StatSigContext.Provider value={{ refreshStatSigContext, contextState: updateState }}>
      <PlatformFeatureGateResolver>{children}</PlatformFeatureGateResolver>
    </StatSigContext.Provider>
  );
};

const useStatSigUpdateContext = (): { refreshStatSigContext: () => void; contextState: boolean } => {
  const context = useContext(StatSigContext);
  if (context === undefined) {
    throw new Error("useStatSigContext must be used within a StatSigUpdateProvider");
  }
  return context;
};

export const useCheckStatSigFeatureGate = (): ((featureGate: STATSIG_FEATURE_GATES) => boolean) => {
  const { contextState } = useStatSigUpdateContext();
  return useCallback(
    (featureGate: STATSIG_FEATURE_GATES) => {
      return FeatureGates.checkGate(featureGate);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextState], // this will cause a re-render of components that depend on this hook, meaning they will re-check the feature gate with updated experiment values
  );
};

export const useCheckStatsigGetAllFeatureGates = (): (() => Record<string, boolean>) => {
  const { contextState } = useStatSigUpdateContext();
  return useCallback(
    () => {
      const allFeatureGates: Record<string, boolean> = {};

      // Loop through all enum values
      Object.values(STATSIG_FEATURE_GATES).forEach((featureGate) => {
        allFeatureGates[featureGate] = FeatureGates.checkGate(featureGate);
      });

      return allFeatureGates;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextState], // this will cause a re-render of components that depend on this hook, meaning they will re-check the feature gate with updated experiment values
  );
};

const hasDynamicConfigValue = <T,>(configResult: unknown): configResult is { value: T } => {
  return typeof configResult === "object" && configResult !== null && "value" in configResult;
};

export const useStatsigBooleanDynamicConfig = (configKey: string): boolean => {
  const { contextState } = useStatSigUpdateContext();

  return useMemo(() => {
    const configResult = getConfigClient()?.getBoolean(configKey);
    return hasDynamicConfigValue<boolean>(configResult) ? configResult.value : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, contextState]);
};

export const useStatsigStringDynamicConfig = (configKey: string, fallbackValue = ""): string => {
  const { contextState } = useStatSigUpdateContext();

  return useMemo(() => {
    const configResult = getConfigClient()?.getString(configKey);
    return hasDynamicConfigValue<string>(configResult) ? configResult.value : fallbackValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, fallbackValue, contextState]);
};

export const useStatsigNumberDynamicConfig = (configKey: string, fallbackValue?: number): number | undefined => {
  const { contextState } = useStatSigUpdateContext();

  return useMemo(() => {
    const configResult = getConfigClient()?.getNumber(configKey);
    return hasDynamicConfigValue<number>(configResult) ? configResult.value : fallbackValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, fallbackValue, contextState]);
};

export const useStatsigStringListDynamicConfig = (
  configKey: string,
  fallbackValue?: string[],
): string[] | undefined => {
  const { contextState } = useStatSigUpdateContext();

  return useMemo(() => {
    const configResult = getConfigClient()?.getStringList(configKey);
    return hasDynamicConfigValue<string[]>(configResult) ? configResult.value : fallbackValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, fallbackValue, contextState]);
};

export type DynamicConfigType = "boolean" | "string" | "number" | "string []" | "unknown";

export interface DynamicConfigListItem {
  name: string;
  value: boolean | string | number | string[] | "Not found";
  type: DynamicConfigType;
  enumKey: string | undefined;
  statsigUrl?: string;
  switcherooUrl?: string;
}

export interface ListedDynamicConfigsResult {
  isInitialized: boolean;
  listedDynamicConfigs: DynamicConfigListItem[];
}

export const useListDynamicConfigs = (): ListedDynamicConfigsResult => {
  const { contextState } = useStatSigUpdateContext();

  return useMemo(() => {
    const configClient = getConfigClient();
    if (!configClient) {
      return {
        isInitialized: false,
        listedDynamicConfigs: [],
      };
    }

    return {
      isInitialized: true,
      listedDynamicConfigs: Object.entries(STATSIG_DYNAMIC_CONFIGS).map(([enumKey, configName]) => {
        const configNameString = String(configName);
        const booleanValueResult = configClient.getBoolean(configNameString);
        const stringValueResult = configClient.getString(configNameString);
        const numberValueResult = configClient.getNumber(configNameString);
        const stringListValueResult = configClient.getStringList(configNameString);

        const booleanValue = hasDynamicConfigValue<boolean>(booleanValueResult) ? booleanValueResult.value : undefined;
        const stringValue = hasDynamicConfigValue<string>(stringValueResult) ? stringValueResult.value : undefined;
        const numberValue = hasDynamicConfigValue<number>(numberValueResult) ? numberValueResult.value : undefined;
        const stringListValue = hasDynamicConfigValue<string[]>(stringListValueResult)
          ? stringListValueResult.value
          : undefined;

        if (typeof booleanValue === "boolean") {
          return {
            name: configNameString,
            value: booleanValue,
            type: "boolean" as const,
            enumKey,
            statsigUrl: getStatsigUrl(configNameString, "dynamicConfig"),
            switcherooUrl: getSwitcherooUrl(configNameString, "dynamicConfig"),
          };
        }

        if (typeof stringValue === "string") {
          return {
            name: configNameString,
            value: `"${stringValue}"`,
            type: "string" as const,
            enumKey,
            statsigUrl: getStatsigUrl(configNameString, "dynamicConfig"),
            switcherooUrl: getSwitcherooUrl(configNameString, "dynamicConfig"),
          };
        }

        if (typeof numberValue === "number") {
          return {
            name: configNameString,
            value: numberValue,
            type: "number" as const,
            enumKey,
            statsigUrl: getStatsigUrl(configNameString, "dynamicConfig"),
            switcherooUrl: getSwitcherooUrl(configNameString, "dynamicConfig"),
          };
        }

        if (Array.isArray(stringListValue) && stringListValue.every((item) => typeof item === "string")) {
          return {
            name: configNameString,
            value: stringListValue,
            type: "string []" as const,
            enumKey,
            statsigUrl: getStatsigUrl(configNameString, "dynamicConfig"),
            switcherooUrl: getSwitcherooUrl(configNameString, "dynamicConfig"),
          };
        }

        return {
          name: configNameString,
          value: "Not found" as const,
          type: "unknown" as const,
          enumKey,
          statsigUrl: getStatsigUrl(configNameString, "dynamicConfig"),
          switcherooUrl: getSwitcherooUrl(configNameString, "dynamicConfig"),
        };
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextState]);
};

export const setConfigClientForTesting = (configClient: ReturnType<typeof getConfigClient>): void => {
  configClientInstance = configClient;
};

export const resetConfigClientForTesting = (): void => {
  configClientInstance = null;
};

const PlatformFeatureGateResolver = ({ children }: PropsWithChildren<{}>): React.JSX.Element => {
  setPlatformFeatureFlagResolver();

  return <>{children}</>;
};
